import {
	ReactElement,
	useCallback
} from "react";
import { AvatarPlaceholderValue, AvatarSelector } from "./AvatarSelector";
import {
	IconButton,
	PersonAvatarStatusValue,
	Space
} from "client/Components";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { useFileSelector } from "client/Hooks";
import styles from "./PersonalInformationBanner.module.less";

export type PersonalInformationBannerProps = {
	fullName: string;
	identifiesAs?: string | null;
	info?: string | ReactElement;
	avatarUrl?: string;
	avatarPlaceholder: AvatarPlaceholderValue;
	backgroundImageUrl?: string;
	actions?: ReactElement | null;
	trackingId: string;
} & (
	| {
		onChangeAvatar(file: File): void;
		avatarContentTypes?: readonly string[];
		status?: undefined;
	}
	| {
		onChangeAvatar?: undefined;
		avatarContentTypes?: undefined;
		status?: PersonAvatarStatusValue;
	}
) & (
	| {
		onChangeBackgroundImage(file: File | null): void;
		backgroundImageContentTypes?: readonly string[];
	}
	| {
		onChangeBackgroundImage?: undefined;
		backgroundImageContentTypes?: undefined;
	}
);

export const PersonalInformationBanner = (props: PersonalInformationBannerProps): ReactElement => {
	const {
		fullName,
		identifiesAs,
		avatarUrl,
		avatarPlaceholder,
		backgroundImageUrl,
		info,
		onChangeAvatar,
		avatarContentTypes,
		onChangeBackgroundImage,
		backgroundImageContentTypes,
		actions,
		status,
		trackingId
	} = props;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			<BackgroundImage
				currentImageUrl={backgroundImageUrl}
				onChange={onChangeBackgroundImage}
				contentTypes={backgroundImageContentTypes}
			/>
			<div className={styles.content}>
				<div className={styles.avatarContainer}>
					<div className={styles.avatarPositionContainer}>
						<AvatarSelector
							avatarUrl={avatarUrl}
							placeholder={avatarPlaceholder}
							trackingId="avatar-selector"
							{...(isNotNil(onChangeAvatar)
								? {
									isReadonly: false,
									onChange: onChangeAvatar,
									supportedContentTypes: avatarContentTypes
								}
								: {
									isReadonly: true,
									status: status
								}
							)}
						/>
					</div>
				</div>
				<div className={styles.contentInner}>
					<div className={styles.contentContainer}>
						<p className={styles.title}>
							<span
								{...setTrackingId("name")}
								className={styles.name}
							>
								{fullName}
								<Space />
							</span>
							{isNotNil(identifiesAs) && (
								<span
									{...setTrackingId("identifies-as")}
									className={styles.identifiesAs}
								>
									{identifiesAs}
								</span>
							)}
						</p>
						<p
							{...setTrackingId("info")}
							className={styles.info}
						>
							{info}
						</p>
					</div>
					{isNotNil(actions) && (
						<div className={styles.actionsContainer}>
							{actions}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
PersonalInformationBanner.displayName = "PersonalInformationBanner";

type BackgroundImageProps = {
	currentImageUrl: string | undefined;
	onChange?(file: File | null): void;
	contentTypes?: readonly string[];
};

const BackgroundImage = (props: BackgroundImageProps): ReactElement => {
	const {
		currentImageUrl,
		onChange,
		contentTypes
	} = props;

	const changeHandler = useCallback(
		(file: File): void => {
			onChange?.(file);
		},
		[onChange]
	);

	const {
		open
	} = useFileSelector({
		isMultiple: false,
		onChange: changeHandler,
		contentTypes
	});

	return (
		<div
			{...setTrackingId("background-image")}
			style={{
				backgroundImage: isNotNil(currentImageUrl)
					? `url(${currentImageUrl})`
					: undefined
			}}
			className={styles.backgroundImage}
		>
			{onChange && (
				<>
					<div className={styles.backgroundImageActions}>
						<IconButton
							icon="camera"
							kind="secondary"
							trackingId="upload-background-image"
							shape="round"
							size="large"
							onClick={open}
						/>
						{currentImageUrl && (
							<IconButton
								icon="trash-can"
								kind="secondary"
								trackingId="delete-background-image"
								shape="round"
								size="large"
								onClick={() => onChange(null)}
							/>
						)}
					</div>
				</>
			)}
		</div>
	);
};
BackgroundImage.displayName = "BackgroundImage";